/**
 * Notification Service — LOCAL (frontend-only) implementation.
 *
 * Persists notifications + history in localStorage. Mirrors the method
 * signatures of notificationService.api.js so all hooks/pages swap for free.
 *
 * Notification shape:
 *   { id, title, message, type, isRead, createdAt, userId,
 *     category, link }
 *
 * History shape:
 *   { id, notificationId, templateId, templateName, recipientId,
 *     recipient, title, type, status, createdAt }
 *
 * Contract:
 *   getAll(params)           → Promise<{ notifications, total, page, limit }>
 *   getMine(userId, params)  → Promise<{ notifications, total, unreadCount }>
 *   getById(id)              → Promise<notification>
 *   create(data)             → Promise<notification>
 *   markRead(id)             → Promise<notification>
 *   markAllRead(userId)      → Promise<{ updated: number }>
 *   remove(id)               → Promise<{ id }>
 *   getUnreadCount(userId)   → Promise<number>
 *
 *   getHistory(params)       → Promise<{ history, total, page, limit }>
 *   addHistory(data)         → Promise<historyEntry>
 */

const STORE_KEY   = 'agrocast_notifications'
const HISTORY_KEY = 'agrocast_notification_history'
const LATENCY     = 80

// ── Persistence ───────────────────────────────────────────────────────────────

function read(key) {
  try {
    const raw = localStorage.getItem(key)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch { return [] }
}

function write(key, data) {
  try { localStorage.setItem(key, JSON.stringify(data)) }
  catch (err) {
    if (err?.name === 'QuotaExceededError') throw Object.assign(new Error('Storage limit reached.'), { status: 507 })
    throw err
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const delay = (ms) => new Promise((r) => setTimeout(r, ms))

function uid() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `ntf_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
}

function err(msg, status = 400) {
  return Object.assign(new Error(msg), { status })
}

function applyFilters(list, { search, type, isRead, userId, page = 1, limit = 20 }) {
  let rows = [...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

  if (userId)              rows = rows.filter((n) => n.userId === userId || n.userId === 'all')
  if (type && type !== 'all')  rows = rows.filter((n) => n.type === type)
  if (isRead === 'unread') rows = rows.filter((n) => !n.isRead)
  if (isRead === 'read')   rows = rows.filter((n) => n.isRead)

  if (search) {
    const q = search.toLowerCase()
    rows = rows.filter((n) =>
      n.title?.toLowerCase().includes(q) ||
      n.message?.toLowerCase().includes(q),
    )
  }

  const total = rows.length
  const start = (page - 1) * limit
  return { rows: rows.slice(start, start + limit), total }
}

// ── Service ───────────────────────────────────────────────────────────────────

const notificationLocalService = {

  // Admin: all notifications
  async getAll(params = {}) {
    await delay(LATENCY)
    const all = read(STORE_KEY)
    const { rows, total } = applyFilters(all, params)
    const { page = 1, limit = 20 } = params
    return { notifications: rows, total, page, limit }
  },

  // Portal: current user's notifications
  async getMine(userId, params = {}) {
    await delay(LATENCY)
    const all = read(STORE_KEY)
    const { rows, total } = applyFilters(all, { ...params, userId })
    const unreadCount = all.filter((n) => !n.isRead && (n.userId === userId || n.userId === 'all')).length
    return { notifications: rows, total, unreadCount }
  },

  async getById(id) {
    await delay(LATENCY)
    const n = read(STORE_KEY).find((n) => n.id === id)
    if (!n) throw err('Notification not found', 404)
    return n
  },

  async create(data) {
    await delay(LATENCY)
    const now = new Date().toISOString()
    const notification = {
      id:        uid(),
      title:     data.title     ?? 'Notification',
      message:   data.message   ?? '',
      type:      data.type      ?? 'info',
      category:  data.category  ?? 'system',
      userId:    data.userId    ?? 'all',
      link:      data.link      ?? null,
      isRead:    false,
      createdAt: now,
      updatedAt: now,
    }
    const all = read(STORE_KEY)
    all.push(notification)
    write(STORE_KEY, all)

    // Auto-add to history
    await notificationLocalService.addHistory({
      notificationId: notification.id,
      templateId:     data.templateId   ?? null,
      templateName:   data.templateName ?? null,
      recipientId:    data.userId       ?? 'all',
      recipient:      data.recipientName ?? (data.userId === 'all' ? 'All Users' : data.userId),
      title:          notification.title,
      type:           'in-app',
      status:         'sent',
    })

    return notification
  },

  async markRead(id) {
    await delay(LATENCY)
    const all = read(STORE_KEY)
    const idx = all.findIndex((n) => n.id === id)
    if (idx === -1) throw err('Notification not found', 404)
    all[idx] = { ...all[idx], isRead: true, updatedAt: new Date().toISOString() }
    write(STORE_KEY, all)
    return all[idx]
  },

  async markAllRead(userId) {
    await delay(LATENCY)
    const all = read(STORE_KEY)
    let updated = 0
    const next = all.map((n) => {
      if (!n.isRead && (n.userId === userId || n.userId === 'all')) {
        updated++
        return { ...n, isRead: true, updatedAt: new Date().toISOString() }
      }
      return n
    })
    write(STORE_KEY, next)
    return { updated }
  },

  async remove(id) {
    await delay(LATENCY)
    const all  = read(STORE_KEY)
    const next = all.filter((n) => n.id !== id)
    if (next.length === all.length) throw err('Notification not found', 404)
    write(STORE_KEY, next)
    return { id }
  },

  async getUnreadCount(userId) {
    await delay(LATENCY)
    const all = read(STORE_KEY)
    return all.filter((n) => !n.isRead && (n.userId === userId || n.userId === 'all')).length
  },

  // ── History ──────────────────────────────────────────────────────────────────

  async getHistory(params = {}) {
    await delay(LATENCY)
    const all = read(HISTORY_KEY)
    const sorted = [...all].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    const { page = 1, limit = 20, search, type, status } = params

    let rows = sorted
    if (type && type !== 'all')     rows = rows.filter((h) => h.type === type)
    if (status && status !== 'all') rows = rows.filter((h) => h.status === status)
    if (search) {
      const q = search.toLowerCase()
      rows = rows.filter((h) =>
        h.title?.toLowerCase().includes(q) ||
        h.recipient?.toLowerCase().includes(q) ||
        h.templateName?.toLowerCase().includes(q),
      )
    }

    const total = rows.length
    const start = (page - 1) * limit
    return { history: rows.slice(start, start + limit), total, page, limit }
  },

  async addHistory(data) {
    await delay(20)
    const entry = {
      id:             uid(),
      notificationId: data.notificationId ?? null,
      templateId:     data.templateId     ?? null,
      templateName:   data.templateName   ?? null,
      recipientId:    data.recipientId    ?? 'all',
      recipient:      data.recipient      ?? 'Unknown',
      title:          data.title          ?? '',
      type:           data.type           ?? 'in-app',   // 'in-app' | 'email'
      status:         data.status         ?? 'sent',     // 'sent' | 'failed' | 'pending'
      createdAt:      new Date().toISOString(),
    }
    const all = read(HISTORY_KEY)
    all.push(entry)
    write(HISTORY_KEY, all)
    return entry
  },
}

export default notificationLocalService
